import { useNavigate } from 'react-router';
import { useState, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useAuth, useLogout } from 'src/hooks/useAuth';

import { CONTENT_MAX_WIDTH } from 'src/constants';

import { NavBar, MobileNavBar } from 'src/components/navbar';
import {
  ProfileColourModal,
  DEFAULT_PROFILE_COLOUR,
} from 'src/components/profile-colour-modal/ProfileColourModal';

type AppLayoutProps = {
  children: ReactNode;
  title: string;
  onBack?: () => void;
  action?: ReactNode;
  hideDesktopTitle?: boolean;
  /**
   * Desktop only: let the page own its heading instead of rendering the layout
   * title. Used by the add-activity wizard, which shows its title above the
   * progress bar.
   */
  desktopFullNav?: boolean;
  alignContentToLeft?: boolean;
};

export default function AppLayout({
  children,
  title,
  onBack,
  action,
  hideDesktopTitle = false,
  desktopFullNav = false,
  alignContentToLeft = false,
}: AppLayoutProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const handleBack = onBack ?? (() => navigate(-1));
  const [colourModalOpen, setColourModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { logout } = useLogout();

  if (isDesktop) {
    return (
      <>
        <NavBar
          isAuthenticated={isAuthenticated}
          user={user}
          onSignOut={logout}
          avatarColour={user?.profileColour ?? DEFAULT_PROFILE_COLOUR}
          onAvatarClick={isAuthenticated ? () => setColourModalOpen(true) : undefined}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              maxWidth: alignContentToLeft ? 'none' : CONTENT_MAX_WIDTH,
              mx: alignContentToLeft ? 0 : 'auto',
              width: '100%',
              minWidth: 0,
              px: alignContentToLeft ? 0 : { md: 3, lg: 4 },
              py: 4,
            }}
          >
            {!hideDesktopTitle && !desktopFullNav && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Typography variant="h2" component="h1" sx={{ flex: 1, letterSpacing: 0.1 }}>
                  {title}
                </Typography>
                {action}
              </Box>
            )}
            {children}
          </Box>
        </Box>
        <ProfileColourModal open={colourModalOpen} onClose={() => setColourModalOpen(false)} />
      </>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        pt: 3,
      }}
    >
      <MobileNavBar variant="page" title={title} onBack={handleBack} />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          overflowY: 'auto',
          px: 3,
          py: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
